import * as React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    Typography,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    FormControlLabel,
    Switch,
    InputAdornment,
    Divider
} from '@mui/material';

import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';

import AdminLayout from '../components/AdminLayout';
import Api from '../services/Api';
import { EmojiEventsOutlined, SearchOutlined } from '@mui/icons-material';

export interface ITournamentDto {
    id: number;
    name: string | null;
    description: string | null;
    imageUrl: string | null;
    startDate: string; // ISO date
    endDate: string;   // ISO date
    isActive: boolean;
}

export interface ITournamentsPageProps { }

export interface ITournamentsPageState {
    tournaments: ITournamentDto[];
    loading: boolean;
    error: string | null;
    searchText: string;
    dialogOpen: boolean;
    dialogMode: 'create' | 'edit';
    editingTournament: ITournamentDto | null;
}

/**
 * Diálogo para alta / edición de un Tournament
 */
interface ITournamentDialogProps {
    open: boolean;
    mode: 'create' | 'edit';
    tournament: ITournamentDto | null;
    onClose: () => void;
    onSaved: () => void;
}

interface ITournamentDialogState {
    name: string;
    description: string;
    imageUrl: string;
    startDate: string; // yyyy-MM-dd
    endDate: string;   // yyyy-MM-dd
    isActive: boolean;
    saving: boolean;
    error: string | null;
}

class TournamentDialog extends React.Component<
    ITournamentDialogProps,
    ITournamentDialogState
> {
    public constructor(props: ITournamentDialogProps) {
        super(props);

        const t = props.tournament;

        this.state = {
            name: t?.name || '',
            description: t?.description || '',
            imageUrl: t?.imageUrl || '',
            startDate: t ? TournamentDialog.toInputDate(t.startDate) : '',
            endDate: t ? TournamentDialog.toInputDate(t.endDate) : '',
            isActive: t?.isActive ?? true,
            saving: false,
            error: null
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleToggleActive = this.handleToggleActive.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    public componentDidUpdate(prevProps: ITournamentDialogProps): void {
        // Si se abre el diálogo o cambia el torneo, reseteamos el estado
        if (
            this.props.open &&
            (!prevProps.open ||
                prevProps.tournament?.id !== this.props.tournament?.id ||
                prevProps.mode !== this.props.mode)
        ) {
            const t = this.props.tournament;
            this.setState({
                name: t?.name || '',
                description: t?.description || '',
                imageUrl: t?.imageUrl || '',
                startDate: t ? TournamentDialog.toInputDate(t.startDate) : '',
                endDate: t ? TournamentDialog.toInputDate(t.endDate) : '',
                isActive: t?.isActive ?? true,
                saving: false,
                error: null
            });
        }
    }

    private static toInputDate(dateString: string): string {
        // Esperamos algo tipo "2025-11-29T00:00:00" y devolvemos "2025-11-29"
        if (!dateString) return '';
        return dateString.substring(0, 10);
    }

    private validate(): string | null {
        const { name, description, startDate, endDate } = this.state;
        if (!name.trim()) return 'El nombre es obligatorio.';
        if (!description.trim()) return 'La descripción es obligatorio.';
        if (!startDate) return 'La fecha de inicio es obligatoria.';
        if (!endDate) return 'La fecha de fin es obligatoria.';

        const start = new Date(startDate);
        const end = new Date(endDate);
        if (end < start) return 'La fecha de fin no puede ser anterior a la de inicio.';

        return null;
    }

    private handleChange(
        field: 'name' | 'description' | 'imageUrl' | 'startDate' | 'endDate'
    ) {
        return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            this.setState({ [field]: event.target.value } as Pick<
                ITournamentDialogState,
                typeof field
            >);
        };
    }

    private handleToggleActive(
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ): void {
        this.setState({ isActive: checked });
    }

    private async handleSave(): Promise<void> {
        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }

        this.setState({ saving: true, error: null });

        try {
            const { mode, tournament } = this.props;
            const { name, description, imageUrl, startDate, endDate, isActive } =
                this.state;

            const body = {
                name,
                description,
                imageUrl,
                startDate, // "yyyy-MM-dd" -> el backend lo parsea como DateTime
                endDate,
                isActive
            };

            if (mode === 'create') {
                const response = await Api.post('/api/Tournaments', body);
                if (!response.ok) {
                    throw new Error('No se pudo crear el torneo.');
                }
            } else {
                if (!tournament) {
                    throw new Error('No se ha especificado el torneo a editar.');
                }
                const response = await Api.put(`/api/Tournaments/${tournament.id}`, body);
                if (!response.ok) {
                    throw new Error('No se pudo actualizar el torneo.');
                }
            }

            this.props.onSaved();
        } catch (err: any) {
            this.setState({
                saving: false,
                error: err.message || 'Error al guardar el torneo.'
            });
        }
    }

    public render(): React.ReactElement<ITournamentDialogProps> {
        const { open, onClose, mode } = this.props;
        const {
            name,
            description,
            imageUrl,
            startDate,
            endDate,
            isActive,
            saving,
            error
        } = this.state;

        const title = mode === 'create' ? 'Nuevo torneo' : 'Editar torneo';

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{title}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}

                        <TextField
                            label="Nombre"
                            fullWidth
                            value={name}
                            onChange={this.handleChange('name')}
                            required
                        />

                        <TextField
                            label="Descripción"
                            fullWidth
                            multiline
                            required
                            minRows={3}
                            value={description}
                            onChange={this.handleChange('description')}
                        />

                        <TextField
                            label="URL de imagen"
                            fullWidth
                            value={imageUrl}
                            onChange={this.handleChange('imageUrl')}
                            placeholder="https://..."
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Fecha inicio"
                                type="date"
                                fullWidth
                                value={startDate}
                                onChange={this.handleChange('startDate')}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Fecha fin"
                                type="date"
                                fullWidth
                                value={endDate}
                                onChange={this.handleChange('endDate')}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Stack>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isActive}
                                    onChange={this.handleToggleActive}
                                    color="primary"
                                />
                            }
                            label="Torneo activo"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={this.handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Button onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default class TournamentsPage extends React.Component<
    ITournamentsPageProps,
    ITournamentsPageState
> {
    public constructor(props: ITournamentsPageProps) {
        super(props);

        this.state = {
            tournaments: [],
            loading: false,
            error: null,
            searchText: '',
            dialogOpen: false,
            dialogMode: 'create',
            editingTournament: null
        };

        this.loadTournaments = this.loadTournaments.bind(this);
        this.handleCreate = this.handleCreate.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.goToMiniGames = this.goToMiniGames.bind(this);
        this.handleDialogClose = this.handleDialogClose.bind(this);
        this.handleDialogSaved = this.handleDialogSaved.bind(this);
    }

    public componentDidMount(): void {
        this.loadTournaments();
    }

    private async loadTournaments(): Promise<void> {
        this.setState({ loading: true, error: null });

        try {
            const response = await Api.get('/api/Tournaments');

            if (!response.ok) {
                throw new Error('No se pudo cargar la lista de torneos.');
            }

            const data: ITournamentDto[] = await response.json();

            this.setState({
                tournaments: data,
                loading: false,
                error: null,
                searchText: ''
            });
        } catch (error: any) {
            this.setState({
                loading: false,
                error: error.message || 'Error al cargar los torneos.'
            });
        }
    }

    private handleCreate(): void {
        this.setState({
            dialogOpen: true,
            dialogMode: 'create',
            editingTournament: null
        });
    }

    private handleEdit(tournament: ITournamentDto): void {
        this.setState({
            dialogOpen: true,
            dialogMode: 'edit',
            editingTournament: tournament
        });
    }

    private async handleDelete(tournament: ITournamentDto): Promise<void> {
        const confirmar = window.confirm(
            `¿Seguro que deseas eliminar el torneo "${tournament.name}"?`
        );
        if (!confirmar) {
            return;
        }

        try {
            const response = await Api.delete(`/api/Tournaments/${tournament.id}`);

            if (!response.ok) {
                throw new Error('No se pudo eliminar el torneo.');
            }

            await this.loadTournaments();
        } catch (error: any) {
            alert(error.message || 'Error al eliminar el torneo.');
        }
    }

    private goToMiniGames(tournament: ITournamentDto): void {
        window.location.href = `/games?tournamentId=${tournament.id}`;
    }

    private goToAdminPrizes(tournament: ITournamentDto): void {
        window.location.href = `/adminleaderboard?tournamentId=${tournament.id}`;
    }

    private goToTvLeaderBoard(tournament: ITournamentDto): void {
        window.location.href = `/leaderboard?tournamentId=${tournament.id}`;
    }

    private goToAdminLeaderBoard(tournament: ITournamentDto): void {
        window.location.href = `/ranking?tournamentId=${tournament.id}`;
    }

    private handleSearchChange(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void {
        this.setState({ searchText: event.target.value });
    }

    private matchesSearch(t: ITournamentDto, term: string): boolean {
        const q = term.trim().toLowerCase();
        if (!q) return true;

        const parts: string[] = [];

        if (t.name) parts.push(t.name);
        if (t.description) parts.push(t.description);
        if (t.imageUrl) parts.push(t.imageUrl);

        parts.push(t.startDate);
        parts.push(this.formatDate(t.startDate));
        parts.push(t.endDate);
        parts.push(this.formatDate(t.endDate));
        parts.push(t.isActive ? 'si sí activo true' : 'no inactivo false');

        return parts.some((p) => p.toLowerCase().includes(q));
    }

    private handleDialogClose(): void {
        this.setState({ dialogOpen: false, editingTournament: null });
    }

    private async handleDialogSaved(): Promise<void> {
        this.setState({ dialogOpen: false, editingTournament: null });
        await this.loadTournaments();
    }

    private renderToolbar(): React.ReactNode {
        const { searchText } = this.state;
        const adorn = (icon: React.ReactNode) => ({
            startAdornment: <InputAdornment position="start">{icon}</InputAdornment>
        });

        return (
            <>
                <Button
                    variant="contained"
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={this.handleCreate}
                >
                    Nuevo
                </Button>

                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar torneo..."
                    value={searchText}
                    onChange={this.handleSearchChange}
                    InputProps={adorn(<SearchOutlined />)}
                    sx={{
                        ml: 'auto',
                        minWidth: { xs: '100%', sm: 220, md: 260 },
                        mt: { xs: 1, sm: 0 }
                    }}
                />
            </>
        );
    }

    private formatDate(dateString: string): string {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString();
    }

    private renderGrid(): React.ReactNode {
        const { tournaments, loading, error, searchText } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Typography color="error" variant="body1">
                    {error}
                </Typography>
            );
        }

        if (!tournaments || tournaments.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay torneos todavía.
                </Typography>
            );
        }

        const filtered = tournaments.filter((t) =>
            this.matchesSearch(t, searchText)
        );

        if (filtered.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay torneos que coincidan con la búsqueda.
                </Typography>
            );
        }

        return (
            <Grid container spacing={3}>
                {filtered.map((t) => (
                    <Grid key={t.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: 4
                            }}
                        >
                            {t.imageUrl ? (
                                <CardMedia
                                    component="img"
                                    height="160"
                                    image={t.imageUrl}
                                    alt={t.name || 'Tournament image'}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        height: 160,
                                        bgcolor: 'grey.300',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <EmojiEventsIcon sx={{ fontSize: 48, color: 'grey.600' }} />
                                </Box>
                            )}

                            <CardContent sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" fontWeight={500} gutterBottom noWrap>
                                    {t.name || 'Sin nombre'}
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {this.formatDate(t.startDate)} – {this.formatDate(t.endDate)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color={t.isActive ? 'success.main' : 'error.main'}
                                        sx={{ ml: 1 }}
                                    >
                                        {t.isActive ? 'Activo' : 'Inactivo'}
                                    </Typography>
                                </Box>

                                {t.description && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {t.description}
                                    </Typography>
                                )}
                            </CardContent>

                            <CardActions
                                sx={{
                                    mt: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    pb: 2
                                }}
                            >
                                <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} width={'100%'}>
                                    <Button
                                        size="small"
                                        onClick={() => this.handleEdit(t)}
                                    >
                                        Editar
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => this.handleDelete(t)}
                                    >
                                        Eliminar
                                    </Button>
                                </Stack>
                                <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} width={'100%'}>
                                    <Button
                                        size="small"
                                        onClick={() => this.goToMiniGames(t)}
                                    >
                                        Juegos
                                    </Button>

                                    <Button
                                        size="small"
                                        onClick={() => this.goToAdminPrizes(t)}
                                    >
                                        Asignación de premios
                                    </Button>
                                </Stack>
                                <Divider sx={{my: 2}}/>
                                <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} width={'100%'} display={{xs: 'none', md: 'flex'}}>
                                    <Button
                                        size="small"
                                        variant='contained'
                                        fullWidth
                                        onClick={() => this.goToTvLeaderBoard(t)}
                                    >
                                        Panel de clasificación
                                    </Button>
                                </Stack>
                                <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} width={'100%'} display={{md: 'none'}}>
                                    <Button
                                        size="small"
                                        variant='contained'
                                        fullWidth
                                        onClick={() => this.goToAdminLeaderBoard(t)}
                                    >
                                        Panel de clasificación
                                    </Button>
                                </Stack>

                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    }

    public render(): React.ReactElement<ITournamentsPageProps> {
        const { dialogOpen, dialogMode, editingTournament } = this.state;

        return (
            <>
                <AdminLayout
                    entityTitle="Torneos"
                    entityIcon={<EmojiEventsOutlined sx={{ fontSize: '56px' }} />}
                    entityToolbar={this.renderToolbar()}
                >
                    {this.renderGrid()}
                </AdminLayout>

                <TournamentDialog
                    open={dialogOpen}
                    mode={dialogMode}
                    tournament={editingTournament}
                    onClose={this.handleDialogClose}
                    onSaved={this.handleDialogSaved}
                />
            </>
        );
    }
}
